import React, { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import workshop_bg from "../../assets/img/workshop/workshop_bg.jpg";
import "../../assets/css/workshop.css";

// Icons from Workshop
import expert_icon from "../../assets/icons/expert-instructer.png";
import hands_on_lab from "../../assets/icons/hands-on-lab.png";
import industry_certificate from "../../assets/icons/industry-certificate.png";
import flexiable_learn from "../../assets/icons/flexiable-learning.png";
import placement_icon from "../../assets/icons/palcement-icon.png";
import industry_icon from "../../assets/icons/industry-icon.png";
import clock_icon from "../../assets/icons/clock-icon.png";

import plc_icon from "../../assets/icons/career/electrical&automation-icon.png";
import scada_icon from "../../assets/icons/career/it-icon.png";
import hmi_iterface from "../../assets/icons/career/HMI Interface.png";
import iot_industry from "../../assets/icons/career/IoT & Industry 4.0 Integration.png";
import maintanance_icon from "../../assets/icons/career/Maintenance-icon.png";
import embadded_icon from "../../assets/icons/career/embedded-icon.png";
import vfd_icon from "../../assets/icons/career/VFD Programming.png";
import certificate from "../../assets/icons/career/certificate.png";
import seo_tech from "../../assets/icons/career/SEO Techniques.png";
import social_market from "../../assets/icons/career/Social Media Marketing.png";
import content_strategy from "../../assets/icons/career/Content Strategy.png";
import ppc_icon from "../../assets/icons/career/it-icon.png";
import dataAnalytics_icon from "../../assets/icons/career/Data Analytics.png";
import email_manage from "../../assets/icons/career/mail Campaign Management.png";
import diractapllication_icon from "../../assets/icons/career/Direct Application to Live Projects.png";
import about_us from "../../assets/img/workshop/workshop_aboutus.jpg";

// meta Data
import { Helmet } from "react-helmet";
import data from "../ApiUrl/meta_api.js";

// ================================================================
//  FRAMER-MOTION VARIANTS
// ================================================================

// Fade up — headings, paragraphs, sections
const fadeUp = {
  hidden: { opacity: 0, y: 50 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94] },
  },
};

// Slide in from left — about image, CTA left col
const fadeLeft = {
  hidden: { opacity: 0, x: -60 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94] },
  },
};

// Slide in from right — about text, CTA right col
const fadeRight = {
  hidden: { opacity: 0, x: 60 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94] },
  },
};

// Stagger parent — each child animates one after the other
const staggerContainer = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.1 },
  },
};

// Card pop — used inside stagger grids
const cardPop = {
  hidden: { opacity: 0, y: 40, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.5, ease: "easeOut" },
  },
};

// Zoom in — hero title entrance
const zoomIn = {
  hidden: { opacity: 0, scale: 0.85 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] },
  },
};

// Shared viewport config — triggers once when 15% visible
const VP = { once: true, amount: 0.15 };

// ================================================================
//  DATA
// ================================================================

const marketingTopics = [
  {
    icon: seo_tech,
    title: "SEO Techniques",
    desc: "Master the latest search engine optimisation strategies.",
  },
  {
    icon: social_market,
    title: "Social Media Marketing",
    desc: "Craft high-impact campaigns across Meta, Instagram, LinkedIn.",
  },
  {
    icon: content_strategy,
    title: "Content Strategy",
    desc: "Build compelling narratives and content calendars.",
  },
  {
    icon: ppc_icon,
    title: "PPC Advertising",
    desc: "Run cost-effective Google Ads and Meta Ads campaigns.",
  },
  {
    icon: dataAnalytics_icon,
    title: "Data Analytics & GA4",
    desc: "Use Google Analytics 4 to turn data into decisions.",
  },
  {
    icon: email_manage,
    title: "Email Campaign Management",
    desc: "Design automated email sequences.",
  },
  {
    icon: diractapllication_icon,
    title: "Direct Application to Live Projects",
    desc: "Apply every strategy to real-time campaigns.",
  },
  {
    icon: certificate,
    title: "Professional Certification",
    desc: "Receive a verified digital marketing certificate.",
  },
];

const automationTopics = [
  {
    icon: plc_icon,
    title: "PLC Programming",
    desc: "Hands-on exercises with Mitsubishi, Siemens, and Allen-Bradley PLCs.",
  },
  {
    icon: scada_icon,
    title: "SCADA Development",
    desc: "Create and manage real-time industrial automation systems.",
  },
  {
    icon: hmi_iterface,
    title: "HMI Interface Design",
    desc: "Design interactive, user-friendly control panels.",
  },
  {
    icon: iot_industry,
    title: "IoT & Industry 4.0 Integration",
    desc: "Explore how Industrial IoT is revolutionising automation.",
  },
  {
    icon: maintanance_icon,
    title: "Maintenance & Troubleshooting",
    desc: "Practical strategies to diagnose faults and reduce downtime.",
  },
  {
    icon: embadded_icon,
    title: "Embedded Systems Design",
    desc: "Develop real-time industrial automation solutions.",
  },
  {
    icon: vfd_icon,
    title: "VFD Programming & Implementation",
    desc: "Configure and optimise Variable Frequency Drives.",
  },
  {
    icon: certificate,
    title: "Certification of Participation",
    desc: "Earn an industry-recognised certificate.",
  },
];

const whyCards = [
  {
    icon: expert_icon,
    title: "Expert Instructors",
    desc: "Learn from certified automation engineers with 10+ years of hands-on industry experience.",
  },
  {
    icon: hands_on_lab,
    title: "Hands-On Lab Training",
    desc: "Practice on genuine industrial hardware — Siemens, Allen-Bradley, Mitsubishi, Schneider.",
  },
  {
    icon: industry_certificate,
    title: "Industry-Recognised Certification",
    desc: "Earn certifications valued by manufacturing and process industries across India.",
  },
  {
    icon: flexiable_learn,
    title: "Flexible Learning Modes",
    desc: "Classroom, live online, or hybrid training to fit your schedule.",
  },
  {
    icon: placement_icon,
    title: "Placement Assistance",
    desc: "Resume building, mock interviews, and direct job referrals.",
  },
  {
    icon: industry_icon,
    title: "Industry 4.0 Curriculum",
    desc: "Updated syllabus covering IIoT, Industry 4.0 and latest automation technologies.",
  },
];

const faqItems = [
  {
    q: "What is industrial automation training and why is it important?",
    a: "Industrial automation training equips engineers and technicians with skills to design, program, operate, and maintain automated systems using PLCs, SCADA, HMI, robotics, and industrial networks.",
  },
  {
    q: "Who should enrol in these courses?",
    a: "These programs are ideal for B.E./B.Tech graduates, diploma holders, ITI students, and working professionals seeking skill upgrades or a domain switch into the automation industry.",
  },
  {
    q: "What certifications will I receive?",
    a: "You receive an industry-recognised certification from Skimitar Hindusthan, aligned with real-world competencies tested by employers.",
  },
  {
    q: "Are courses available in online mode?",
    a: "Yes. We offer in-person classroom training at our Salem and Erode centres, live online sessions, and hybrid modes.",
  },
  {
    q: "What career roles can I target?",
    a: "PLC Engineer, SCADA Operator, Automation Technician, Control Systems Engineer, DCS Engineer, Maintenance Engineer, and Industrial IoT Developer.",
  },
  {
    q: "Is placement support included?",
    a: "Yes. Every enrolled student receives dedicated placement support including resume building, mock interviews and job referrals.",
  },
];

// ================================================================
//  SECTION HEADER
// ================================================================
function SectionHeader({ label, title, subtitle, sub }) {
  return (
    <div className="mb-5">
      {label && (
        <span
          className="fw-semibold d-flex"
          style={{ fontSize: "20px", color: "#cc0000" }}
        >
          {label}
        </span>
      )}
      <div className="mt-2 d-flex ws-titles justify-content-start">
        <span className="mt-2 justify-content-start">{title}</span>
        {subtitle && (
          <span className="ms-2" style={{ color: "#cc0000" }}>
            {subtitle}
          </span>
        )}
      </div>
      {sub && <p className="mt-3 mb-0 d-flex">{sub}</p>}
    </div>
  );
}

// ================================================================
//  TOPIC CARD — motion.div wraps the col
// ================================================================
function TopicCard({ item }) {
  return (
    <motion.div className="col-12 col-md-6 col-lg-3 mb-4" variants={cardPop}>
      <div className="card h-100 border-0 shadow-sm rounded-3 ws-topic-card">
        <div className="card-body p-4 text-center d-flex flex-column">
          <center>
            <img
              src={item.icon}
              className="mb-3 text-center"
              style={{ width: "35px" }}
              alt={item.title}
            />
          </center>
          <h5 className="fw-bold mb-3 ws-topic-title">{item.title}</h5>
          <p className="text-muted flex-grow-1 ws-topic-desc">{item.desc}</p>
        </div>
      </div>
    </motion.div>
  );
}

// ================================================================
//  MAIN COMPONENT
// ================================================================
const WorkShop = () => {
  const [meta, setMeta] = useState(null);

  const fetchData = () => {
    const WorkShopMeta = data[0]?.workshop_page;
    if (WorkShopMeta) {
      setMeta(WorkShopMeta);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <div className="ws-page" style={{ fontFamily: "'Poppins', sans-serif" }}>
      <Helmet>
        <title>{meta ? meta.title : "Workshop"}</title>
      </Helmet>

      {/* ── 1. HERO ────────────────────────────────────────── */}
      <section className="ws-hero-section position-relative overflow-hidden">
        <div
          className="ws-hero-bg position-absolute w-100 h-100"
          style={{
            backgroundImage: `linear-gradient(rgba(0,0,0,0.65), rgba(0,0,0,0.75)), url(${workshop_bg})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />

        <div className="container position-relative" style={{ zIndex: 2 }}>
          {/* Badge */}
          <motion.div
            className="d-flex align-items-center justify-content-center gap-3 mb-3"
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            transition={{ delay: 0.1 }}
          >
            <span className="text-white fw-semibold px-3 py-1 rounded-4 ws-hero-badge">
              Industrial Automation Workshops
            </span>
          </motion.div>

          {/* Title */}
          <motion.h1
            className="text-white text-center fw-bold mb-3 ws-hero-title"
            variants={zoomIn}
            initial="hidden"
            animate="visible"
            transition={{ delay: 0.25 }}
          >
            Hands-On Workshops.
            <br />
            <em style={{ color: "#cc0000" }}>Real Skills. Real Careers.</em>
          </motion.h1>

          {/* Sub */}
          <motion.p
            className="text-white text-center mx-auto ws-hero-sub"
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            transition={{ delay: 0.45 }}
          >
            Intensive training in Industrial Automation & Digital Marketing with
            real equipment and live projects.
          </motion.p>
        </div>
      </section>

      {/* ── 2. ABOUT ────────────────────────────────────────── */}
      <div className="container ws-container mb-4 mt-5 mb-md-5 px-3 px-md-4">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={VP}
        >
          <SectionHeader
            label="About US"
            title="Workshops: Hands-On Learning for Real-World Impact"
          />
        </motion.div>

        <div className="row">
          {/* Image slides from left */}
          <motion.div
            className="col-md-6 col-sm-12"
            variants={fadeLeft}
            initial="hidden"
            whileInView="visible"
            viewport={VP}
          >
            <img
              src={about_us}
              className="img-fluid rounded-4"
              alt="About Workshop"
            />
          </motion.div>

          {/* Text slides from right */}
          <motion.div
            className="col-md-6 col-sm-12"
            variants={fadeRight}
            initial="hidden"
            whileInView="visible"
            viewport={VP}
          >
            <div className="ms-3">
              <p>
                <b>Skimitar Hindusthan</b> Training Institute, our workshops are
                designed to provide participants with practical, hands-on
                training in Industrial Automation and Digital Marketing. With a
                focus on real-world applications, these workshops empower
                individuals to gain in-depth knowledge and build skills that are
                immediately applicable in their careers.
              </p>
              <p>
                We are a leading Industrial Automation training institute
                committed to delivering industry-focused, practical education
                for students and professionals. Our training programs are
                designed to meet the growing demand for skilled automation
                engineers in modern industries.
              </p>
              <p>
                By combining practical learning, modern technology, and career
                support, we aim to create skilled professionals who can succeed
                in today's competitive industrial automation field.
              </p>
              <p>
                We also provide career-oriented guidance, including resume
                building, interview preparation, and placement support. Our goal
                is to help students become job-ready Industrial Automation
                Engineers, PLC Programmers, and Control System Professionals.
              </p>
            </div>
          </motion.div>
        </div>
      </div>

      {/* ── 3. WHY CHOOSE US ────────────────────────────────── */}
      <section className="py-5 bg_color ws-section-gap">
        <div className="container ws-container">
          <motion.div
            className="mb-5 text-center"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={VP}
          >
            <SectionHeader
              label="Why US"
              title="Why Choose Skimitar Hindusthan Workshops?"
            />
          </motion.div>

          <motion.div
            className="row g-4"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={VP}
          >
            {whyCards.map((item, i) => (
              <motion.div
                key={i}
                className="col-md-6 col-lg-4"
                variants={cardPop}
              >
                <div className="card h-100 border-0 shadow-sm rounded-3 p-4 text-center ws-card-hover">
                  <img
                    src={item.icon}
                    alt={item.title}
                    style={{
                      width: "35px",
                      height: "35px",
                      margin: "0 auto 15px",
                    }}
                  />
                  <h5 className="ws-card-title fw-bold mb-3">{item.title}</h5>
                  <p style={{ fontSize: "15px" }} className="ws-card-des">
                    {item.desc}
                  </p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── 4. INDUSTRIAL AUTOMATION TOPICS ────────────────── */}
      <section className="py-5 ws-section-gap">
        <div className="container ws-container">
          <motion.div
            className="mb-5 text-center"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={VP}
          >
            <SectionHeader
              label="Workshop Track 01"
              title="Industrial Automation Workshops"
            />
          </motion.div>

          <motion.div
            className="row g-4"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={VP}
          >
            {automationTopics.map((item, i) => (
              <TopicCard key={i} item={item} />
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── 5. DIGITAL MARKETING TOPICS ─────────────────────── */}
      <section className="py-5 ws-section-gap bg_color">
        <div className="container ws-container">
          <motion.div
            className="mb-5 text-center"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={VP}
          >
            <SectionHeader
              label="Workshop Track 02"
              title="Digital Marketing Workshops"
            />
          </motion.div>

          <motion.div
            className="row g-4"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={VP}
          >
            {marketingTopics.map((item, i) => (
              <TopicCard key={i} item={item} />
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── 6. FAQ ───────────────────────────────────────────── */}
      <section className="py-5 ws-section-gap">
        <div className="container ws-container">
          <motion.div
            className="mb-5 text-center"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={VP}
          >
            <SectionHeader label="FAQ" title="Frequently Asked Questions" />
          </motion.div>

          <motion.div
            className="accordion ws-accordion"
            id="faqAccordion"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={VP}
          >
            {faqItems.map((item, i) => (
              <motion.div
                className="accordion-item border-0 shadow-sm mb-3"
                key={i}
                variants={fadeUp}
              >
                <h2 className="accordion-header">
                  <button
                    className={`accordion-button fw-semibold ${i !== 0 ? "collapsed" : ""}`}
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target={`#faq${i}`}
                  >
                    {item.q}
                  </button>
                </h2>
                <div
                  id={`faq${i}`}
                  className={`accordion-collapse collapse ${i === 0 ? "show" : ""}`}
                  data-bs-parent="#faqAccordion"
                >
                  <div
                    className="accordion-body"
                    style={{ fontSize: "15px", lineHeight: "1.75" }}
                  >
                    {item.a}
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── 7. CTA ───────────────────────────────────────────── */}
      <motion.section
        style={{ marginTop: "100px" }}
        variants={fadeUp}
        initial="hidden"
        whileInView="visible"
        viewport={VP}
      >
        <div className="p-5 ws-cta-wrap">
          <div className="d-flex align-items-center justify-content-center gap-3 mb-3">
            <div
              className="bg-light"
              style={{ width: "40px", height: "1px" }}
            />
            <span
              className="text-uppercase fw-semibold text-white text-opacity-75"
              style={{
                letterSpacing: "2px",
                whiteSpace: "nowrap",
                fontSize: "13px",
              }}
            >
              Start Your Automation Career
            </span>
            <div
              className="bg-light"
              style={{ width: "40px", height: "1px" }}
            />
          </div>

          <div className="row g-4 align-items-center">
            <motion.div
              className="col-lg-7 text-center text-lg-start"
              variants={fadeLeft}
              initial="hidden"
              whileInView="visible"
              viewport={VP}
            >
              <h2 className="fw-bolder text-white text-uppercase mb-3 ws-cta-title">
                Your Automation Career Starts at <b>Skimitar Hindusthan</b>
              </h2>
              <p
                className="text-white text-opacity-75 mb-0"
                style={{ lineHeight: "1.75" }}
              >
                Join thousands of students who chose structured automation
                training, built real hands-on skills, and launched successful
                careers — with <b>Skimitar Hindusthan's</b> practical,
                industry-aligned programs and dedicated placement support at
                every step.
              </p>
            </motion.div>

            <motion.div
              className="col-lg-5"
              variants={fadeRight}
              initial="hidden"
              whileInView="visible"
              viewport={VP}
            >
              <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center justify-content-lg-end">
                <button className="btn ws-btn-white px-4 py-3 fw-bold text-uppercase">
                  Explore All Courses →
                </button>
                <button className="btn ws-btn-ghost px-4 py-3 fw-bold text-uppercase">
                  📞 (+91) 8428010909
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.section>
    </div>
  );
};

export default WorkShop;
